import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"
import { AlertCircle, Check, Clock, ExternalLink, Sparkles, X } from "lucide-react"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/portal/PageHeader"
import { FilterBar } from "@/components/portal/FilterBar"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { cn } from "@/lib/utils"

type ActionItem = NonNullable<Awaited<ReturnType<typeof fetchActions>>>["items"][number]

async function fetchActions(tenant: string, status: string) {
  const { data, error } = await api.GET("/api/v1/actions", {
    params: { query: { tenant: tenant || undefined, status, limit: 200 } },
  })
  if (error) throw new Error(String(error))
  return data
}

const PRIORITY_ORDER: Record<string, number> = {
  critical: 0, high: 1, medium: 2, low: 3,
}
const PRIORITY_META: Record<string, { label: string; color: string; bg: string; ring: string }> = {
  critical: { label: "Kritisch", color: "#dc2626", bg: "rgba(239,68,68,0.08)", ring: "ring-red-500/30" },
  high:     { label: "Hoch",     color: "#dc2626", bg: "rgba(239,68,68,0.08)", ring: "ring-red-500/20" },
  medium:   { label: "Mittel",   color: "#92400e", bg: "rgba(245,158,11,0.12)", ring: "ring-amber-500/20" },
  low:      { label: "Niedrig",  color: "#64748b", bg: "rgba(100,116,139,0.1)", ring: "ring-slate-400/20" },
}

/**
 * Meister.AI — Actions-zentriertes Dashboard.
 * Lebt parallel zum klassischen Kunden-Dashboard. Zielbild aus Plattform-Vision:
 * "User bestätigt nur noch" — jede Card hat AI-Recommendation + 3 Quick-Actions.
 */
export default function MeisterAI() {
  const [tenant, setTenant] = useTenantFilter()
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["actions", tenant, "open"],
    queryFn: () => fetchActions(tenant, "open"),
  })
  const doneData = useQuery({
    queryKey: ["actions", tenant, "done-today-count"],
    queryFn: async () => {
      const d = await fetchActions(tenant, "done")
      const today = new Date().toISOString().slice(0, 10)
      return d.items.filter((i) => (i.created_at || "").slice(0, 10) === today).length
    },
    staleTime: 1000 * 60,
  })

  const groups = useMemo(() => {
    const byPrio = new Map<string, ActionItem[]>()
    for (const it of data?.items ?? []) {
      const p = (it.priority || "medium").toLowerCase()
      const arr = byPrio.get(p) ?? []
      arr.push(it)
      byPrio.set(p, arr)
    }
    return Array.from(byPrio.entries()).sort(
      ([a], [b]) => (PRIORITY_ORDER[a] ?? 99) - (PRIORITY_ORDER[b] ?? 99),
    )
  }, [data])

  const kpis = useMemo(() => {
    const counts = (data?.counts_by_priority ?? {}) as Record<string, number>
    const urgent = (counts.critical ?? 0) + (counts.high ?? 0)
    return {
      urgent,
      total: data?.total ?? 0,
      doneToday: doneData.data ?? 0,
    }
  }, [data, doneData.data])

  if (error) return <div className="p-10"><ErrorBox error={error} /></div>

  return (
    <div className="px-10 py-8 max-w-[1240px]">
      <PageHeader title="Meister.AI" onRefresh={() => refetch()} refreshing={isFetching} />

      {/* Intro + KPIs */}
      <div className="mb-6">
        <p className="text-muted-foreground text-[14px] mb-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          {kpis.urgent > 0
            ? `Sie haben ${kpis.urgent} dringende Vorgänge und ${kpis.total} insgesamt.`
            : kpis.total > 0
            ? `${kpis.total} Vorgänge vorbereitet — nichts dringendes.`
            : "Alles erledigt! Keine offenen Aktionen."}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Dringend" value={kpis.urgent} tint="red" />
          <KpiCard label="Offen gesamt" value={kpis.total} tint="indigo" />
          <KpiCard label="Heute erledigt" value={kpis.doneToday} tint="green" />
          <KpiCard
            label="Kategorien"
            value={new Set((data?.items ?? []).map((i) => i.category || "—")).size}
            tint="amber"
          />
        </div>
      </div>

      <FilterBar tenant={tenant} onTenantChange={setTenant} />

      {isLoading ? (
        <Loading label="Lädt Meister.AI..." />
      ) : (data?.items.length ?? 0) === 0 ? (
        <div className="p-12 text-center text-muted-foreground rounded-lg bg-card border">
          <Sparkles className="w-8 h-8 mx-auto mb-3 text-primary/40" />
          <div className="font-medium text-foreground">Alles erledigt.</div>
          <div className="text-[13px] mt-1">Keine offenen Aktionen — gut gemacht!</div>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([prio, items]) => {
            const meta = PRIORITY_META[prio] ?? PRIORITY_META.medium
            return (
              <section key={prio}>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4" style={{ color: meta.color }} />
                  <h2 className="text-[15px] font-semibold" style={{ color: meta.color }}>
                    {meta.label}
                  </h2>
                  <span
                    className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: meta.bg, color: meta.color }}
                  >
                    {items.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {items.map((it) => (
                    <ActionCard key={it.id} item={it} tintRing={meta.ring} />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

function KpiCard({ label, value, tint }: { label: string; value: number; tint: "red" | "indigo" | "amber" | "green" }) {
  const styles = {
    red:    { border: "#ef4444", color: "#dc2626" },
    indigo: { border: "#4f46e5", color: "#4f46e5" },
    amber:  { border: "#f59e0b", color: "#d97706" },
    green:  { border: "#10b981", color: "#059669" },
  }[tint]
  return (
    <div
      className="rounded-lg bg-card border-l-4 border-y border-r px-4 py-3 shadow-xs"
      style={{ borderLeftColor: styles.border }}
    >
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="text-[26px] font-bold mt-1" style={{ color: styles.color }}>{value}</div>
    </div>
  )
}

function ActionCard({ item, tintRing }: { item: ActionItem; tintRing: string }) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["actions"] })
  }
  const mutate = useMutation({
    mutationFn: async (body: { status: string; snooze_hours?: number }) => {
      const { error } = await api.PATCH("/api/v1/actions/{action_id}", {
        params: { path: { action_id: item.id } },
        body: { status: body.status, snooze_hours: body.snooze_hours ?? 1 },
      })
      if (error) throw new Error(String(error))
    },
    onSuccess: invalidate,
  })

  // action_url is often /admin/... old-portal link — öffnen im neuen Tab
  const openOld = item.action_url
    ? () => window.open(item.action_url!, "_blank")
    : undefined

  return (
    <div
      className={cn(
        "bg-card rounded-lg border shadow-xs p-4 transition hover:shadow-md ring-1 ring-transparent",
        "hover:" + tintRing,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[14px] font-semibold leading-snug">{item.title}</h3>
            {item.category && (
              <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-medium">
                {item.category}
              </span>
            )}
            {item.tenant_slug && (
              <span className="text-[10.5px] text-muted-foreground">· {item.tenant_slug}</span>
            )}
          </div>
          {item.description && (
            <p className="text-[12.5px] text-muted-foreground mt-1.5 whitespace-pre-wrap">
              {item.description}
            </p>
          )}
          {item.ai_recommendation && (
            <div className="mt-2 p-2.5 rounded-md bg-primary/5 border border-primary/10 flex gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <div className="text-[12.5px] leading-relaxed">
                <span className="font-semibold text-primary">KI-Empfehlung:</span>{" "}
                {item.ai_recommendation}
              </div>
            </div>
          )}
          {item.ai_draft && (
            <details className="mt-2">
              <summary className="text-[11.5px] text-muted-foreground cursor-pointer hover:text-primary">
                KI-Antwort-Entwurf ansehen
              </summary>
              <div className="mt-1.5 p-2.5 rounded-md bg-secondary/70 text-[12.5px] whitespace-pre-wrap leading-relaxed">
                {item.ai_draft}
              </div>
            </details>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
          {fmtRelative(item.created_at)}
        </div>
      </div>

      {/* Quick-Actions */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {openOld && (
          <button
            onClick={openOld}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Öffnen
          </button>
        )}
        <button
          disabled={mutate.isPending}
          onClick={() => mutate.mutate({ status: "done" })}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-success/10 text-success hover:bg-success/20 disabled:opacity-50"
        >
          <Check className="w-3.5 h-3.5" /> Erledigt
        </button>
        <button
          disabled={mutate.isPending}
          onClick={() => mutate.mutate({ status: "snoozed", snooze_hours: 4 })}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/70 disabled:opacity-50"
        >
          <Clock className="w-3.5 h-3.5" /> Später (4h)
        </button>
        <button
          disabled={mutate.isPending}
          onClick={() => mutate.mutate({ status: "dismissed" })}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md text-muted-foreground hover:bg-secondary disabled:opacity-50"
        >
          <X className="w-3.5 h-3.5" /> Ignorieren
        </button>
        {mutate.isError && (
          <span className="text-[11px] text-destructive">
            Fehler: {mutate.error instanceof Error ? mutate.error.message : "unbekannt"}
          </span>
        )}
      </div>
    </div>
  )
}
